import type { DispatchAssignmentDto } from './contractsMirror.js';

/**
 * The time-grid model behind the week board — where a block sits, how tall it is, and how
 * concurrent blocks share a day's width.
 *
 * The board used to stack assignment cards in a day column in list order and print the time as a
 * line of text. Every assignment already carries `startTime`/`endTime`, so the schedule was fully
 * known and simply not drawn: a nine-hour job and a one-hour job were the same size, a 7am job and
 * a 6pm job sat in the same place, and two crews on the same hour looked identical to two crews
 * twelve hours apart. This module turns those times back into geometry.
 *
 * It is deliberately pure — no React, no DOM, no clock. Everything here is a function of the
 * assignments and the visible range, which is what makes the overlap packing testable.
 */

/**
 * How long a block is drawn when the assignment has a start but no end.
 *
 * `endTime` is nullable and the office does leave it blank. Drawing such a block as zero-height
 * would hide the assignment entirely, which is worse than drawing it slightly wrong, so it gets a
 * nominal length. It is NOT persisted and NOT sent anywhere — the assignment still has no end time,
 * this is only what the grid paints. `hasAssumedEnd` marks those blocks so the UI can say so.
 */
export const DEFAULT_DURATION_MIN = 120;

export const MINUTES_IN_DAY = 24 * 60;

/** Drop targets are half-hour slots; finer than that is not a real dispatch decision. */
export const SLOT_MINUTES = 30;

/** The vertical window the grid paints. */
export interface HourRange {
  startMin: number;
  endMin: number;
}

/**
 * The default window. ServiceTitan paints midnight to midnight and spends most of the screen on
 * hours nobody works — eight empty rows before the first job. The working day fits without
 * scrolling; `FULL_DAY` is one control away for the odd-hours call.
 */
export const WORKING_HOURS: HourRange = { startMin: 6 * 60, endMin: 20 * 60 };

/** The expanded window — the full 24 hours, for on-call and overnight work. */
export const FULL_DAY: HourRange = { startMin: 0, endMin: MINUTES_IN_DAY };

/**
 * Minutes past midnight for an `HH:MM` clock string, or null if it is absent or unparseable.
 *
 * Tolerates a single-digit hour and a trailing `:SS` — the write paths validate `^\d{2}:\d{2}$`,
 * but this also reads rows written before that validator existed, and a row we cannot parse must
 * fall out to the all-day strip rather than land at midnight.
 */
export function parseHhMm(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Minutes past midnight back to the `HH:MM` the API expects. */
export function minutesToHhMm(min: number): string {
  const clamped = Math.max(0, Math.min(min, MINUTES_IN_DAY - 1));
  const hours = `${Math.floor(clamped / 60)}`.padStart(2, '0');
  const minutes = `${clamped % 60}`.padStart(2, '0');
  return `${hours}:${minutes}`;
}

export interface TimedSpan {
  startMin: number;
  endMin: number;
}

/**
 * The span a block occupies, or null when it has no usable start and belongs in the all-day strip.
 *
 * An end at or before the start is treated as absent rather than as a midnight crossing: a shift
 * that genuinely runs past midnight is two calendar days and the board is built per day, so the
 * alternative reading would silently paint a block over the whole day. Clamped to the end of the
 * day so a 23:30 start with the default duration cannot run off the bottom of the column.
 */
export function resolveSpan(
  assignment: Pick<DispatchAssignmentDto, 'startTime' | 'endTime'>,
): TimedSpan | null {
  const startMin = parseHhMm(assignment.startTime);
  if (startMin === null) return null;
  const parsedEnd = parseHhMm(assignment.endTime);
  const endMin =
    parsedEnd !== null && parsedEnd > startMin ? parsedEnd : startMin + DEFAULT_DURATION_MIN;
  return { startMin, endMin: Math.min(endMin, MINUTES_IN_DAY) };
}

/** True when the block is drawn at a nominal length because the assignment has no real end time. */
export function hasAssumedEnd(
  assignment: Pick<DispatchAssignmentDto, 'startTime' | 'endTime'>,
): boolean {
  const startMin = parseHhMm(assignment.startTime);
  if (startMin === null) return false;
  const parsedEnd = parseHhMm(assignment.endTime);
  return parsedEnd === null || parsedEnd <= startMin;
}

export interface PlacedBlock {
  assignment: DispatchAssignmentDto;
  startMin: number;
  endMin: number;
  /** 0-based column within the overlap cluster. */
  lane: number;
  /** How many columns the cluster splits into — the block's width is 1/laneCount. */
  laneCount: number;
}

/**
 * One day's assignments laid out into non-overlapping lanes, plus the untimed ones.
 *
 * Three crews out at 10am must read as three side-by-side blocks, which means every block needs to
 * know both its own column and how many columns its neighbours forced. That is a two-pass problem:
 * a block's width is decided by the widest point of the CLUSTER it belongs to, not by its own
 * overlaps. So blocks are swept in start order into clusters of transitively-overlapping work, each
 * taking the first lane free at its start time, and the lane count is stamped on the whole cluster
 * once it closes.
 *
 * Sorting is total (start, then end, then id) so the same day always lays out the same way — a
 * board whose columns reshuffle between polls is unreadable.
 */
export function layoutDay(assignments: DispatchAssignmentDto[]): {
  placed: PlacedBlock[];
  untimed: DispatchAssignmentDto[];
} {
  const untimed: DispatchAssignmentDto[] = [];
  const timed: Array<{ assignment: DispatchAssignmentDto; span: TimedSpan }> = [];

  for (const assignment of assignments) {
    const span = resolveSpan(assignment);
    if (span === null) untimed.push(assignment);
    else timed.push({ assignment, span });
  }

  timed.sort(
    (a, b) =>
      a.span.startMin - b.span.startMin ||
      a.span.endMin - b.span.endMin ||
      a.assignment.id - b.assignment.id,
  );

  const placed: PlacedBlock[] = [];
  let cluster: PlacedBlock[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;

  function closeCluster() {
    for (const block of cluster) block.laneCount = laneEnds.length;
    placed.push(...cluster);
    cluster = [];
    laneEnds = [];
    clusterEnd = -1;
  }

  for (const { assignment, span } of timed) {
    // A block starting at or after everything currently open begins a fresh cluster — nothing in
    // the previous one can constrain its width.
    if (cluster.length > 0 && span.startMin >= clusterEnd) closeCluster();

    let lane = laneEnds.findIndex((end) => end <= span.startMin);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = span.endMin;

    cluster.push({ assignment, startMin: span.startMin, endMin: span.endMin, lane, laneCount: 1 });
    clusterEnd = Math.max(clusterEnd, span.endMin);
  }
  if (cluster.length > 0) closeCluster();

  return { placed, untimed };
}

/**
 * The hour boundaries to rule a line at, inside `range`.
 *
 * The END of the range is deliberately excluded: a mark there is the closing edge of the grid, not
 * the start of an hour, and the column already draws that border. Including it also labelled the
 * full-day view "12 AM" twice — once at 00:00 and again at 24:00, which reads as a grid that wraps.
 */
export function hourMarks(range: HourRange): number[] {
  const marks: number[] = [];
  for (let min = Math.ceil(range.startMin / 60) * 60; min < range.endMin; min += 60) {
    marks.push(min);
  }
  return marks;
}

/** The half-hour drop targets inside `range`. */
export function slotStarts(range: HourRange): number[] {
  const slots: number[] = [];
  for (let min = range.startMin; min < range.endMin; min += SLOT_MINUTES) slots.push(min);
  return slots;
}

/**
 * Blocks that fall entirely outside the visible window.
 *
 * The collapsed working-day view is a convenience, not a filter: a 5am start must not silently
 * vanish from the board. The count drives the "N outside these hours" control that expands to the
 * full day.
 */
export function countOutsideRange(blocks: PlacedBlock[], range: HourRange): number {
  return blocks.filter((b) => b.endMin <= range.startMin || b.startMin >= range.endMin).length;
}

export interface BlockPosition {
  topPct: number;
  heightPct: number;
  leftPct: number;
  widthPct: number;
}

/**
 * A block's position as percentages of its day column.
 *
 * Vertically clamped to the window so a block that starts before it or ends after it is drawn
 * flush to the edge rather than overflowing the grid; a block wholly outside collapses to zero
 * height and is filtered by the caller (see `countOutsideRange`).
 */
export function blockPosition(block: PlacedBlock, range: HourRange): BlockPosition {
  const total = range.endMin - range.startMin;
  const top = Math.max(0, block.startMin - range.startMin);
  const bottom = Math.min(total, block.endMin - range.startMin);
  return {
    topPct: (top / total) * 100,
    heightPct: (Math.max(bottom - top, 0) / total) * 100,
    leftPct: (block.lane / block.laneCount) * 100,
    widthPct: (1 / block.laneCount) * 100,
  };
}

/**
 * An hour-gutter label. Built on a fixed UTC instant so `Intl` decides 12- vs 24-hour from the
 * active locale without the host timezone shifting the number being labelled.
 */
export function formatHourLabel(min: number, locale?: string): string {
  return formatClock(min, locale);
}

/** A block's `9 AM – 5 PM` range, minutes shown only when they are not on the hour. */
export function formatSpanLabel(startMin: number, endMin: number, locale?: string): string {
  return `${formatClock(startMin, locale)} – ${formatClock(endMin, locale)}`;
}

function formatClock(min: number, locale?: string): string {
  const date = new Date(Date.UTC(2000, 0, 1, Math.floor(min / 60) % 24, min % 60));
  return date.toLocaleTimeString(locale, {
    hour: 'numeric',
    ...(min % 60 === 0 ? {} : { minute: '2-digit' }),
    timeZone: 'UTC',
  });
}
